/**
 * Client-side session storage (W2, docs/SECURITY.md).
 *
 * The persisted session holds only the short-lived id/access tokens plus the
 * decoded id-token claims — NEVER the refresh token, which lives in an HttpOnly
 * cookie the auth-proxy sets and JavaScript cannot read. Because the value is no
 * longer a durable credential, it is stored as plaintext JSON: the former AES
 * "obfuscation" (a hardcoded key compiled into the bundle) added no real
 * protection and is gone.
 *
 * Migration is a clean break: a legacy AES-encrypted blob is not valid JSON, so
 * `getJWT` returns null and the user re-authenticates once (usually a
 * transparent Cognito redirect, since the hosted-UI session is still alive).
 */

const SESSION_KEY = 'tunasong.auth.session'

import type { AuthData } from './auth-data.js'

export const setJWT = (auth: AuthData) => {
  /** Defensively drop any refresh token before persisting — it must never touch localStorage. */
  const { refreshToken: _refreshToken, ...safe } = auth
  localStorage.setItem(SESSION_KEY, JSON.stringify(safe))
}

/** Remove local JWT values */
export const clearJWT = () => {
  localStorage.removeItem(SESSION_KEY)
}

export const getJWT = (): AuthData | null => {
  const raw = localStorage.getItem(SESSION_KEY)
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as AuthData
  } catch {
    /** Not valid JSON — e.g. a legacy AES blob. Treat as no session (clean re-login). */
    return null
  }
}
